import Vue from 'vue'
import VueRouter, { RouteConfig, NavigationGuard } from 'vue-router'
import HomeView from '../views/HomeView.vue'
import NodeDetail from '@/views/NodeDetail.vue'
import NodeDetailV2 from '@/views/NodeDetailV2.vue'
import RoleList from '@/views/Role.vue'
import CostumeShop from '@/views/CostumeShop.vue'
import ShopItems from '@/views/ShopItems.vue'
import Chat from "@/views/Chat.vue";
import ChatSock from "@/views/ChatSock.vue";
import {getToken} from "@/utils/auth";
import Login from "@/views/Login.vue";
import HomePage from '@/views/HomePage.vue'
import RecordList from '@/views/RecordList.vue'
import Privacy from '@/views/Privacy.vue'
import UserAgreement from '@/views/UserAgreement.vue'
import MinorProtection from '@/views/MinorProtection.vue'
import Complaint from '@/views/Complaint.vue'
import ChurnAnalysis from '@/views/ChurnAnalysis.vue'
import NewDataAnalysis from '@/views/NewDataAnalysis.vue'
import PlatformNewData from '@/views/PlatformNewData.vue'
import InitialBookNewData from '@/views/InitialBookNewData.vue'
import NovelData from '@/views/NovelData.vue'
import PaymentDataAnalysis from '@/views/PaymentDataAnalysis.vue'
import LogicCheck from '@/views/LogicCheck.vue'
import NodeGraph from '@/views/NodeGraph.vue'
import AuthorEarnings from '@/views/AuthorEarnings.vue'
import AppVersionConfig from '@/views/AppVersionConfig.vue'
import CommentManagement from '@/views/CommentManagement.vue'
import CommentReports from '@/views/CommentReports.vue'
import { getBookDataAccess } from '@/api/bookData'

Vue.use(VueRouter)

// 守卫里读登录用户：路由守卫跑在 App.vue 恢复 Vuex 之前（首屏直接打开 URL 时），所以和下面
// /worklog、/welfare-config 一样读 localStorage 里的 USER。只用来别让没权限的人白跑一趟页面，真正的权限在后端。
function storedUser(): { id?: number; role?: string } | null {
  try {
    const raw = localStorage.getItem('USER')
    return raw ? JSON.parse(raw) : null
  } catch (e) {
    return null
  }
}

// 只有管理员能进的页面（A/B 实验）：同 /welfare-config 的守卫，只是别让没权限的人白跑一趟，真正的权限在 Go 的 AdminAuth
const adminOnly: NavigationGuard = (to, from, next) => {
  if (storedUser()?.role === 'admin') {
    next()
  } else {
    next('/')
  }
}

const routes: Array<RouteConfig> = [
  {
    path: '/',
    name: 'home',
    component: HomePage,
    props: true
  },
  {
    path: '/about',
    name: 'about',
    component: () => import(/* webpackChunkName: "about" */ '../views/AboutView.vue')
  },
  {
    path: "/nodeDetail",
    name: "nodeDetail",
    component: NodeDetail,
    props: true
  },
  {
    path: "/nodeDetailV2",
    name: "nodeDetailV2",
    component: NodeDetailV2,
    props: true
  },
  {
    path: "/nodeGraph",
    name: "nodeGraph",
    component: NodeGraph,
    props: true
  },
  {
    path: "/roles",
    name: "roles",
    component: RoleList,
    props: true
  },
  {
    path: "/costumes",
    name: "costumes",
    component: CostumeShop,
    props: true
  },
  {
    path: "/shop",
    name: "shop",
    component: ShopItems,
    props: true
  },
  {
    path: "/comments",
    name: "Comments",
    component: CommentManagement,
    props: true
  },
  {
    path: "/comment-reports",
    name: "CommentReports",
    component: CommentReports,
    props: true
  },
  {
    path: "/attr-adjust",
    name: "AttrAdjust",
    component: () => import('@/views/AttrAdjust.vue'),
    props: true
  },
  {
    path: "/ai-node-generator",
    name: "AiNodeGenerator",
    component: () => import('@/views/AiNodeGenerator.vue'),
    props: true
  },
  {
    path: "/prompt-debug",
    name: "PromptDebug",
    component: () => import('@/views/PromptDebug.vue'),
    props: true
  },
  {
    path: "/chat",
    name: "chat",
    component: Chat,
    props: true
  },
  {
    path: "/chatSock",
    name: "chatSock",
    component: ChatSock,
    props: true
  },
  {
    path: "/login",
    name: "Login",
    component: Login
  },
  {
    path: '/wx-login-callback',
    name: 'WxLoginCallback',
    component: () => import('@/views/WxLoginCallback.vue')
  },
  {
    path: '/qq-login-callback',
    name: 'QqLoginCallback',
    component: () => import('@/views/QqLoginCallback.vue')
  },
  {
    path: '/records',
    name: 'RecordList',
    component: RecordList
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: Privacy
  },
  {
    path: '/user-agreement',
    name: 'UserAgreement',
    component: UserAgreement
  },
  {
    path: '/minor-protection',
    name: 'MinorProtection',
    component: MinorProtection
  },
  {
    path: '/complaint',
    name: 'Complaint',
    component: Complaint
  },
  {
    path: '/churn-analysis',
    name: 'ChurnAnalysis',
    component: ChurnAnalysis,
    props: true
  },
  {
    // 分书新增数据：2026-10 起只对管理员开放（编辑改看「分书数据」，需加入其白名单）
    path: '/new-data-analysis',
    name: 'NewDataAnalysis',
    component: NewDataAnalysis,
    props: true,
    beforeEnter: (to, from, next) => {
      if (storedUser()?.role === 'admin') {
        next()
      } else {
        next('/')
      }
    }
  },
  {
    // 分书数据（最近 7/30 天 · 所有用户）：管理员，或在白名单里的账号（不限角色）。
    // 白名单只有后端知道，非管理员进来前问一次后端。
    path: '/book-data',
    name: 'BookData',
    component: () => import('@/views/BookData.vue'),
    props: true,
    beforeEnter: async (to, from, next) => {
      if (storedUser()?.role === 'admin' || await getBookDataAccess()) {
        next()
      } else {
        next('/')
      }
    }
  },
  {
    path: '/platform-new-data',
    name: 'PlatformNewData',
    component: PlatformNewData,
    props: true
  },
  {
    path: '/ad-data',
    name: 'AdData',
    component: () => import('@/views/AdData.vue'),
    props: true
  },
  {
    path: '/word-count',
    name: 'WordCount',
    component: () => import('@/views/WordCount.vue'),
    props: true
  },
  {
    path: '/fee-settlement',
    name: 'FeeSettlement',
    component: () => import('@/views/FeeSettlement.vue'),
    props: true
  },
  {
    path: '/novel-data',
    name: 'NovelData',
    component: NovelData,
    props: true
  },
  {
    path: '/initial-book-new-data',
    name: 'InitialBookNewData',
    component: InitialBookNewData,
    props: true
  },
  {
    path: '/payment-data-analysis',
    name: 'PaymentDataAnalysis',
    component: PaymentDataAnalysis,
    props: true
  },
  {
    path: '/logic-check',
    name: 'LogicCheck',
    component: LogicCheck,
    props: true
  },
  {
    path: '/author-earnings',
    name: 'AuthorEarnings',
    component: AuthorEarnings,
    props: true
  },
  {
    path: '/app-version',
    name: 'AppVersionConfig',
    component: AppVersionConfig,
    props: true
  },
  {
    path: '/pending-review',
    name: 'PendingReview',
    component: () => import('@/views/PendingReview.vue'),
    props: true
  },
  {
    path: '/role-management',
    name: 'RoleManagement',
    component: () => import('@/views/RoleManagement.vue')
  },
  {
    // 飞书日报助手配置。只有 admin 角色能进 ——
    // 菜单入口本来就只对 admin 显示，这里再挡一道，防止直接敲 URL 进来。
    // 后端每个接口也会独立校验，前端这两处只是别让页面白跑一趟。
    path: '/worklog',
    name: 'WorklogConfig',
    component: () => import('@/views/WorklogConfig.vue'),
    beforeEnter: (to, from, next) => {
      let role: string | null = null
      try {
        const raw = localStorage.getItem('USER')
        if (raw) role = JSON.parse(raw)?.role ?? null
      } catch (e) {
        role = null
      }
      if (role === 'admin') {
        next()
      } else {
        next('/')
      }
    }
  },
  {
    // 福利配置：只有管理员能进。菜单入口本来就只对 admin 显示，这里再挡一道，防止直接敲 URL；
    // 真正的权限在 Go 的 AdminAuth，前端这道只是别让页面白跑一趟（同 /worklog 的写法）。
    path: '/welfare-config',
    name: 'WelfareConfig',
    component: () => import('@/views/WelfareConfig.vue'),
    beforeEnter: (to, from, next) => {
      let role: string | null = null
      try {
        const raw = localStorage.getItem('USER')
        if (raw) role = JSON.parse(raw)?.role ?? null
      } catch (e) {
        role = null
      }
      if (role === 'admin') {
        next()
      } else {
        next('/')
      }
    }
  },
  {
    // 表白配置（礼物 / 超值礼包 / 表白墙 / 开关），只有管理员能进；真正的权限在 Go 的 AdminAuth。
    // 设计：docs/superpowers/specs/2026-11-02-confession-design.md §6
    path: '/confession-config',
    name: 'ConfessionConfig',
    component: () => import('@/views/ConfessionConfig.vue'),
    beforeEnter: adminOnly
  },
  {
    // A/B 实验（设计：ai-game-backend-go/docs/superpowers/specs/2026-10-19-ab-experiment-platform-design.md §8.1）
    path: '/experiments',
    name: 'ExperimentList',
    component: () => import('@/views/ExperimentList.vue'),
    beforeEnter: adminOnly
  },
  {
    path: '/experiments/calculator',
    name: 'ExperimentCalculator',
    component: () => import('@/views/ExperimentCalculatorPage.vue'),
    beforeEnter: adminOnly
  },
  {
    path: '/experiments/metrics',
    name: 'ExperimentMetrics',
    component: () => import('@/views/ExperimentMetrics.vue'),
    beforeEnter: adminOnly
  },
  {
    path: '/experiments/new',
    name: 'ExperimentNew',
    component: () => import('@/views/ExperimentEdit.vue'),
    beforeEnter: adminOnly
  },
  {
    path: '/experiments/:id(\\d+)/edit',
    name: 'ExperimentEdit',
    component: () => import('@/views/ExperimentEdit.vue'),
    beforeEnter: adminOnly
  },
  {
    path: '/experiments/:id(\\d+)',
    name: 'ExperimentDetail',
    component: () => import('@/views/ExperimentDetail.vue'),
    beforeEnter: adminOnly
  },
  {
    // 推送数据：定时业务推送五类的发送数、点击率（接口见 ai-game-backend-go/docs/push-campaign.md §5）
    path: '/push-data',
    name: 'PushData',
    component: () => import('@/views/PushData.vue'),
    beforeEnter: adminOnly
  },
  {
    // 推送测试：按用户查设备 token、发测试推送（接口见 ai-game-backend-go/docs/push-infrastructure.md §6）
    path: '/push-test',
    name: 'PushTest',
    component: () => import('@/views/PushTest.vue'),
    beforeEnter: adminOnly
  },
  {
    path: '/my-works',
    name: 'MyWorks',
    component: () => import('@/views/MyWorks.vue'),
    props: true
  },
  {
    path: '/games/:gameId/editor',
    name: 'UnifiedEditor',
    component: () => import('@/views/UnifiedEditor.vue'),
    props: true
  },
  {
    path: '/games/:gameId/chapters/:chapterId/graph',
    name: 'ChapterGraph',
    component: () => import('@/views/ChapterGraph.vue'),
    props: true
  },
  {
    path: '/games/:gameId/chapters/:chapterId/calcs',
    name: 'VariableCalcOverview',
    component: () => import('@/views/VariableCalcOverview.vue'),
    props: true
  },
  {
    path: '/games/:gameId/chapters',
    name: 'ChapterList',
    component: () => import('@/views/ChapterList.vue'),
    props: true
  },
  {
    path: '/games/:gameId/chapters/:chapterId',
    name: 'NodeListTree',
    component: () => import('@/views/NodeListTree.vue'),
    props: true
  },
  {
    path: '/games/:gameId/nodes/:nodeId',
    name: 'NodeEditor',
    component: () => import('@/views/NodeEditor.vue'),
    props: true
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

// router.beforeEach((to, from, next) => {
//   if ((to.path !== '/login' && to.path !== '/register') && !getToken()) {
//     next('/login')
//   } else {
//     next()
//   }
// })


export default router
